import { useRef, useState } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import snackbarStore from '../../../../stores/Snackbar.store';
import { SnackbarMessageType } from '../../../../stores/snackbar.types';
import { useTranslation } from '../../../translation/TranslationContext';
import useUpdateOrder from './useUpdateOrder';
import useUpdateOrderInvalidations from './useUpdateOrderInvalidations';

type UseUpdateOrderModalProps = {
  orderId: string;
  onClose: () => void;
};

type UpdateOrderScenario = {
  condition: () => boolean;
  message: string;
  type: SnackbarMessageType;
};

const useUpdateOrderModal = ({ orderId, onClose }: UseUpdateOrderModalProps) => {
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const { invalidateCommonQueries } = useUpdateOrderInvalidations();
  const { validateOrUpdatePrices, validateOrUpdateConditions } = useUpdateOrder();
  const [mustUpdatePrices, setMustUpdatePrices] = useState(false);
  const [mustUpdateConditions, setMustUpdateConditions] = useState(false);
  const hasError = useRef(false);
  const hasUpdatedPrices = useRef(false);
  const hasUpdatedConditions = useRef(false);

  const forceUpdate = true;

  const updateOrderItemPrices = async (): Promise<void> => {
    const response = await validateOrUpdatePrices(orderId, forceUpdate);
    if (response.hasError) {
      hasError.current = true;
    }
    if (response.hasUpdatedPrices) {
      hasUpdatedPrices.current = true;
    }
  };

  const updateOrderConditions = async (): Promise<void> => {
    const response = await validateOrUpdateConditions(orderId, forceUpdate);
    if (response.hasError) {
      hasError.current = true;
    }
    if (response.hasUpdatedConditions) {
      hasUpdatedConditions.current = true;
    }
  };

  const scenarios: UpdateOrderScenario[] = [
    {
      condition: () => hasError.current,
      message: translate(LangEnum.FORCE_UPDATE_ORDER_ERROR_FEEDBACK),
      type: 'error',
    },
    {
      condition: () => hasUpdatedPrices.current && hasUpdatedConditions.current,
      message: translate(LangEnum.FORCE_UPDATE_ORDER_SUCCESS_FEEDBACK),
      type: 'success',
    },
    {
      condition: () => hasUpdatedPrices.current && !hasUpdatedConditions.current,
      message: `${mustUpdatePrices ? translate(LangEnum.FORCE_UPDATE_ORDER_SELLIN_PRICES_SUCCESS_FEEDBACK) + ' ' : ''}${mustUpdateConditions ? translate(LangEnum.FORCE_UPDATE_ORDER_CONDITIONS_DONT_NEED_UPDATING_FEEDBACK) : ''}`,
      type: 'success',
    },
    {
      condition: () => !hasUpdatedPrices.current && hasUpdatedConditions.current,
      message: `${mustUpdateConditions ? translate(LangEnum.FORCE_UPDATE_ORDER_CONDITIONS_SUCCESS_FEEDBACK) + ' ' : ''}${mustUpdatePrices ? translate(LangEnum.FORCE_UPDATE_ORDER_SELLIN_PRICES_DONT_NEED_UPDATING_FEEDBACK) : ''}`,
      type: 'success',
    },
  ];

  const showFeedback = () => {
    for (const scenario of scenarios) {
      if (scenario.condition()) {
        return handleMessage({
          type: scenario.type,
          message: scenario.message,
        });
      }
    }

    return handleMessage({
      type: 'info',
      message: translate(LangEnum.FORCE_UPDATE_ORDER_CONDITIONS_NO_UPDATES_NEEDED_FEEDBACK),
    });
  };

  const resetValues = () => {
    hasError.current = false;
    hasUpdatedPrices.current = false;
    hasUpdatedConditions.current = false;
  };

  const handleUpdateOrder = async () => {
    onClose();

    const promises: Promise<void>[] = [];
    if (mustUpdatePrices) {
      promises.push(updateOrderItemPrices());
    }
    if (mustUpdateConditions) {
      promises.push(updateOrderConditions());
    }
    await Promise.all(promises);

    if (hasUpdatedPrices.current || hasUpdatedConditions.current) {
      invalidateCommonQueries(orderId);
    }
    showFeedback();
    resetValues();
  };

  const onClickPricesCheckbox = () => setMustUpdatePrices(!mustUpdatePrices);
  const onClickConditionsCheckbox = () => setMustUpdateConditions(!mustUpdateConditions);

  return {
    mustUpdatePrices,
    mustUpdateConditions,
    handleUpdateOrder,
    onClickPricesCheckbox,
    onClickConditionsCheckbox,
  };
};

export default useUpdateOrderModal;
